import { Component, effect, inject, input, output, signal, ChangeDetectionStrategy } from '@angular/core';
import { CompanyView, ContactView, SearchContactsForCompanyParameters } from '@api-proxies';
import { IKebabAction } from '@app/components/shared/grids/custom-cell-actions/custom-cell-actions.component';
import { TypeAheadContactsComponent } from '@app/components/shared/type-ahead-contacts/type-ahead-contacts.component';
import { GridService } from '@app/services/grid.service';
import { ContactService } from '@http/clients/contact.service';
import { AwaiterService } from '@services/awaiter.service';
import { ServerSideGridComponent } from '@shared/grids/server-side-grid/server-side-grid.component';
import { ModalConfirmComponent } from '@shared/modals/modal-confirm/modal-confirm.component';
import { ColDef, IServerSideDatasource } from 'ag-grid-enterprise';
import { ModalService, SnackbarNotificationService } from 'ngx-lib';
import { CONTACTS_GRID_COLUMNS } from './companies-single-contacts.config';

@Component({
    selector: 'app-companies-single-contacts',
    templateUrl: './companies-single-contacts.component.html',
    styleUrls: ['./companies-single-contacts.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [ServerSideGridComponent, TypeAheadContactsComponent]
})
export class CompaniesSingleContactsComponent {
    company = input<CompanyView>();

    tabCounterChange = output<void>();
    handleCompanyChange = output<void>();

    contactsDatasource?: IServerSideDatasource;
    noResults = signal<boolean>(false);
    isLoading = signal<boolean>(false);
    gridColumns: ColDef[] = [...CONTACTS_GRID_COLUMNS];

    private readonly contactService = inject(ContactService);
    private readonly awaiter = inject(AwaiterService);
    private readonly modalService = inject(ModalService);
    private readonly snackbarNotificationService = inject(SnackbarNotificationService);
    private readonly gridService = inject(GridService);

    constructor() {
        this.setupGridColumns();

        effect(() => {
            const companyId = this.company()?.id;
            if (companyId && !this.contactsDatasource) {
                this.createDatasource();
            }
        });
    }

    getRowIdFn = (params: any) => params.data.id.toString();

    async setAsMainContact(contact: ContactView): Promise<void> {
        const companyId = this.company()?.id;
        if (!companyId) return;

        if (this.company()?.contactId) {
            const responseOk = await this.modalService.open(ModalConfirmComponent, {
                acceptCaption: 'Confirm',
                cancelCaption: 'Cancel',
                content:
                    "You're about to set a new main contact for this company. This will replace your current main contact with the selected one. Do you wish to continue?",
                title: 'New Main Contact'
            });

            if (!responseOk) return;
        }

        if (!contact) return;

        try {
            const oldMainContactId = this.company()?.contactId;
            await this.contactService.setAsMain(contact.id);
            this.setMainContactInGrid(contact.id, oldMainContactId);
            this.handleCompanyChange.emit();
        } catch (error) {
            console.error('Error setting main contact:', error);
            this.snackbarNotificationService.error('An error occurred while setting the main contact');
        }
    }

    async unsetAsMainContact(contact: ContactView): Promise<void> {
        try {
            await this.contactService.unsetAsMain(contact.id);
            this.unsetMainContactInGrid(contact.id);
            this.handleCompanyChange.emit();
        } catch (error) {
            console.error('Error unsetting main contact:', error);
            this.snackbarNotificationService.error('An error occurred while unsetting the main contact');
        }
    }

    async onSelectedContact(contact: ContactView): Promise<void> {
        const companyId = this.company()?.id;
        if (!contact || !companyId) return;

        contact.companyId = companyId;

        try {
            const contactSaved = await this.contactService.save(contact);

            if (contactSaved) {
                this.tabCounterChange.emit();
                this.refreshGridView();
                this.noResults.set(false);
            }
        } catch (error) {
            console.error(error);
            this.snackbarNotificationService.error('An error occurred while trying to save the contact');
        }
    }

    async removeItem(contact: ContactView): Promise<void> {
        const responseOk = await this.modalService.open(ModalConfirmComponent, {
            acceptCaption: 'Remove contact',
            cancelCaption: 'Cancel',
            content: 'Are you sure you want to remove this contact from the contact list?',
            title: 'Remove contact'
        });

        if (!responseOk) return;

        if (!contact) return;
        contact.companyId = undefined;

        await this.awaiter.awaitAction(
            'Removing contact',
            async () => {
                await this.contactService.save(contact);
                this.tabCounterChange.emit();
                if (contact.isMain) {
                    this.handleCompanyChange.emit();
                }

                this.refreshGridView();
            },
            (loading) => this.isLoading.set(loading)
        );
    }

    async refreshGridView() {
        const gridApi = this.gridService.getGridApi();
        if (!gridApi) return;

        gridApi.refreshServerSide({ purge: true });
    }

    private setupGridColumns(): void {
        const actions: IKebabAction[] = [
            {
                label: 'Set as Main Contact',
                action: (contact: ContactView) => this.setAsMainContact(contact),
                show: (contact: ContactView) => !contact.isMain
            },
            {
                label: 'Unset as Main Contact',
                action: (contact: ContactView) => this.unsetAsMainContact(contact),
                show: (contact: ContactView) => contact.isMain
            },
            {
                label: 'Remove',
                action: (contact: ContactView) => this.removeItem(contact)
            }
        ];

        this.gridColumns = CONTACTS_GRID_COLUMNS.map((col) => {
            if (col.colId === 'actions') {
                return {
                    ...col,
                    cellRendererParams: {
                        actions
                    }
                };
            }
            return col;
        });
    }

    private searchParamsBuilder(gridParams: any): SearchContactsForCompanyParameters {
        const { startRow, sortModel } = gridParams;
        const pageSize = 20;
        const pageNumber = Math.floor(startRow / pageSize) + 1;
        const companyId = this.company()?.id ?? 0;

        const searchParams = {
            pageNumber,
            pageSize,

            ...(sortModel?.length && sortModel[0].sort
                ? {
                      sortBy: sortModel[0].colId,
                      sortDirection: sortModel[0].sort === 'asc' ? 'A' : 'D'
                  }
                : {}),
            companyId
        } as SearchContactsForCompanyParameters;

        return searchParams;
    }

    private createDatasource(): void {
        this.contactsDatasource = {
            getRows: async (params: any) => {
                try {
                    this.isLoading.set(true);
                    this.noResults.set(false);

                    const searchParams = this.searchParamsBuilder(params.request);

                    const response = await this.contactService.searchContactsForCompany(searchParams);

                    if (response && response.results) {
                        const rowData = response.results || [];
                        const totalCount = response.pageInfo?.itemsCount || 0;

                        this.noResults.set(rowData.length === 0 && params.request.startRow === 0);

                        params.success({
                            rowData: rowData,
                            rowCount: totalCount
                        });
                    } else {
                        console.error('Invalid response structure:', response);
                        params.fail();
                        this.noResults.set(true);
                    }
                } catch (error) {
                    console.error('Error fetching contacts:', error);
                    params.fail();
                    this.noResults.set(true);
                } finally {
                    this.isLoading.set(false);
                }
            }
        };
    }

    private unsetMainContactInGrid(contactId: number): void {
        const gridApi = this.gridService.getGridApi();
        if (!gridApi) return;

        const node = gridApi.getRowNode(contactId.toString());
        if (node?.data) {
            node.data.isMain = false;
            gridApi.redrawRows({ rowNodes: [node] });
        }
    }

    private setMainContactInGrid(contactId: number, oldMainContactId?: number): void {
        const gridApi = this.gridService.getGridApi();
        if (!gridApi) return;

        const rowsToUpdate: any[] = [];

        if (oldMainContactId) {
            const oldMainNode = gridApi.getRowNode(oldMainContactId.toString());
            if (oldMainNode?.data) {
                oldMainNode.data.isMain = false;
                rowsToUpdate.push(oldMainNode);
            }
        }

        const newNode = gridApi.getRowNode(contactId.toString());
        if (newNode?.data) {
            newNode.data.isMain = true;
            rowsToUpdate.push(newNode);
        }

        if (rowsToUpdate.length > 0) {
            gridApi.redrawRows({ rowNodes: rowsToUpdate });
        }
    }
}
